import { useCan } from '@twinepos/vue-rbac'
import { computed, ref } from 'vue'

import type { SearchScope, SearchScopeId } from '@/domains/command-center/services/searchService.types'

import { SEARCH_SCOPES } from '@/domains/command-center/services/searchScopes'

/**
 * Which scopes the palette may search, and which of them the user has switched on.
 *
 * The permission read is what makes requirement 2 mechanical rather than cosmetic: `activeScopes`
 * is the only list `searchItems` ever receives, so a scope the user may not see is never asked.
 * The server enforces the same permissions — this spares the user a row that would 403.
 *
 * Fails closed. Until `GET /admin/me/abilities` answers, `can()` is false for everything, so
 * `availableScopes` is empty and the palette shows pages and curated actions only.
 */
export const useSearchScopes = () => {
  const { can } = useCan()

  const deselected = ref<Set<SearchScopeId>>(new Set())
  const selected = ref<Set<SearchScopeId>>(new Set())

  const availableScopes = computed<SearchScope[]>(() =>
    SEARCH_SCOPES.filter(scope => can(scope.permission.action, scope.permission.subject))
  )

  /**
   * Whether the declared defaults survive this user's permissions.
   *
   * They do not for a user who holds only `finance.view`: invoices and bills both default off, so
   * the palette would open with nothing switched on and answer every search with silence. When no
   * available scope defaults on, every available one does.
   */
  const hasDefaultScope = computed(() => availableScopes.value.some(scope => scope.isDefault))

  /**
   * A scope is on when the user switched it on, or when it defaults on and they have not switched
   * it off. Tracking both sets rather than one keeps the default honest: a scope added to
   * `SEARCH_SCOPES` later arrives in its declared state rather than inheriting a stale selection.
   */
  const isActive = (scope: SearchScope): boolean =>
    selected.value.has(scope.id) || ((scope.isDefault || !hasDefaultScope.value) && !deselected.value.has(scope.id))

  const activeScopes = computed<SearchScope[]>(() => availableScopes.value.filter(isActive))

  const isScopeActive = (scopeId: SearchScopeId): boolean => activeScopes.value.some(scope => scope.id === scopeId)

  /**
   * Switching the last scope off would leave a search bar that can find nothing and say nothing
   * about why, so the last one on refuses to go off.
   */
  const toggleScope = (scopeId: SearchScopeId): void => {
    if (isScopeActive(scopeId)) {
      if (activeScopes.value.length === 1) return

      selected.value.delete(scopeId)
      deselected.value.add(scopeId)

      return
    }

    deselected.value.delete(scopeId)
    selected.value.add(scopeId)
  }

  /** Switches a scope on and leaves the others alone. A scan calls this to reach Products. */
  const activateScope = (scopeId: SearchScopeId): void => {
    if (isScopeActive(scopeId)) return

    deselected.value.delete(scopeId)
    selected.value.add(scopeId)
  }

  /** The palette opens the same way every time, so closing it drops whatever was toggled. */
  const resetScopes = (): void => {
    selected.value = new Set()
    deselected.value = new Set()
  }

  return {
    availableScopes,
    activeScopes,
    isScopeActive,
    toggleScope,
    activateScope,
    resetScopes
  }
}
